import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Table, TableRow, TableCell, TableHeader } from "@tiptap/extension-table";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Bold,
  Italic,
  UnderlineIcon,
  Link as LinkIcon,
  ImageIcon,
  Palette,
  Code,
  ClipboardPaste,
} from "lucide-react";

interface SignatureEditorProps {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
}

// Check if a string looks like HTML
const looksLikeHtml = (text: string): boolean => {
  return /<[a-z][\s\S]*>/i.test(text.trim());
};

export const SignatureEditor = ({ value, onChange, disabled }: SignatureEditorProps) => {
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [htmlSource, setHtmlSource] = useState(value);
  // Track whether the current content was set via raw HTML paste
  // so we render it faithfully rather than through Tiptap's schema
  const [rawHtml, setRawHtml] = useState<string | null>(null);
  const rawHtmlRef = useRef<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Keep ref in sync with state so onUpdate callback always has current value
  useEffect(() => { rawHtmlRef.current = rawHtml; }, [rawHtml]);

  // Build a complete HTML document for the iframe so pasted HTML renders
  // with browser defaults only — no Tailwind Preflight interference
  const iframeSrcDoc = useMemo(() => {
    if (!rawHtml) return "";
    return `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>body{margin:8px;font-family:system-ui,-apple-system,sans-serif;font-size:14px;}</style></head><body>${rawHtml}</body></html>`;
  }, [rawHtml]);

  // Auto-size iframe height to match content
  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (iframe?.contentDocument?.body) {
      const height = iframe.contentDocument.body.scrollHeight;
      iframe.style.height = Math.max(120, height + 16) + "px";
    }
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      Table.configure({ resizable: false }),
      TableRow,
      TableCell,
      TableHeader,
      TextStyle,
      Color,
    ],
    content: "",
    editable: !disabled,
    onUpdate: ({ editor }) => {
      // Skip updates while in raw HTML mode — only user edits in WYSIWYG should propagate.
      // Raw mode is cleared explicitly by toolbar buttons, clearRawMode, or toggleHtmlMode.
      if (rawHtmlRef.current !== null) return;
      const html = editor.getHTML();
      onChange(html);
      setHtmlSource(html);
    },
    editorProps: {
      handlePaste: (view, event) => {
        const clipboardData = event.clipboardData;
        if (!clipboardData) return false;

        // Check for rich HTML paste
        const html = clipboardData.getData("text/html");
        if (html && html.trim()) {
          // Rich paste — capture the raw HTML
          event.preventDefault();
          setRawHtml(html);
          onChange(html);
          setHtmlSource(html);
          return true;
        }

        // Check for plain text that looks like HTML
        const text = clipboardData.getData("text/plain");
        if (text && looksLikeHtml(text)) {
          event.preventDefault();
          setRawHtml(text);
          onChange(text);
          setHtmlSource(text);
          return true;
        }

        // Otherwise let Tiptap handle normally
        return false;
      },
    },
  });

  // Sync external value changes into editor
  useEffect(() => {
    if (!editor || value === rawHtml) return;

    // Check if the incoming value is complex HTML that should use raw mode
    const isComplex = value && looksLikeHtml(value) &&
      (value.includes("<table") || value.includes("style=") || value.includes("<img"));

    if (isComplex) {
      setRawHtml(value);
      setHtmlSource(value);
    } else if (value !== editor.getHTML()) {
      setRawHtml(null);
      editor.commands.setContent(value, false);
      setHtmlSource(value);
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  const toggleHtmlMode = useCallback(() => {
    if (isHtmlMode && editor) {
      // Switching from HTML source back to visual
      const newHtml = htmlSource;
      // If it's complex HTML, use raw mode
      if (looksLikeHtml(newHtml) && (newHtml.includes("<table") || newHtml.includes("style="))) {
        setRawHtml(newHtml);
      } else {
        setRawHtml(null);
        editor.commands.setContent(newHtml, false);
      }
      onChange(newHtml);
    } else if (editor) {
      // Switching to HTML source — sync current content
      setHtmlSource(rawHtml || editor.getHTML());
    }
    setIsHtmlMode((prev) => !prev);
  }, [isHtmlMode, htmlSource, rawHtml, editor, onChange]);

  const handlePasteFromClipboard = useCallback(async () => {
    try {
      // Try to read HTML from clipboard via Clipboard API
      const items = await navigator.clipboard.read();
      for (const item of items) {
        if (item.types.includes("text/html")) {
          const blob = await item.getType("text/html");
          const html = await blob.text();
          if (html) {
            setRawHtml(html);
            onChange(html);
            setHtmlSource(html);
            return;
          }
        }
      }
      // Fallback to plain text
      const text = await navigator.clipboard.readText();
      if (text && looksLikeHtml(text)) {
        setRawHtml(text);
        onChange(text);
        setHtmlSource(text);
      } else if (text && editor) {
        editor.commands.insertContent(text);
      }
    } catch {
      // Clipboard API may not be available
    }
  }, [editor, onChange]);

  const addLink = useCallback(() => {
    if (!editor) return;
    const url = window.prompt("Enter URL:");
    if (url) {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
  }, [editor]);

  const addImage = useCallback(() => {
    if (!editor) return;
    const url = window.prompt("Enter image URL:");
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor]);

  const setColor = useCallback(() => {
    if (!editor) return;
    const color = window.prompt("Enter color (e.g. #333333, red):", "#333333");
    if (color) {
      editor.chain().focus().setColor(color).run();
    }
  }, [editor]);

  // Click handler for raw preview to switch to HTML mode for editing
  const handleRawPreviewClick = useCallback(() => {
    setHtmlSource(rawHtml || "");
    setIsHtmlMode(true);
  }, [rawHtml]);

  // When in raw mode and user wants to go back to WYSIWYG editing
  const clearRawMode = useCallback(() => {
    if (editor && rawHtml) {
      editor.commands.setContent(rawHtml, false);
      setRawHtml(null);
    }
  }, [editor, rawHtml]);

  if (!editor) return null;

  return (
    <div className={`border rounded-md ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 p-1.5 border-b bg-muted/30 flex-wrap">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={`h-7 w-7 p-0 ${editor.isActive("bold") ? "bg-muted" : ""}`}
          onClick={() => { setRawHtml(null); editor.chain().focus().toggleBold().run(); }}
          title="Bold"
        >
          <Bold className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={`h-7 w-7 p-0 ${editor.isActive("italic") ? "bg-muted" : ""}`}
          onClick={() => { setRawHtml(null); editor.chain().focus().toggleItalic().run(); }}
          title="Italic"
        >
          <Italic className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={`h-7 w-7 p-0 ${editor.isActive("underline") ? "bg-muted" : ""}`}
          onClick={() => { setRawHtml(null); editor.chain().focus().toggleUnderline().run(); }}
          title="Underline"
        >
          <UnderlineIcon className="h-3.5 w-3.5" />
        </Button>
        <div className="w-px h-5 bg-border mx-0.5" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={`h-7 w-7 p-0 ${editor.isActive("link") ? "bg-muted" : ""}`}
          onClick={addLink}
          title="Add Link"
        >
          <LinkIcon className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={addImage}
          title="Add Image (URL)"
        >
          <ImageIcon className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={setColor}
          title="Text Color"
        >
          <Palette className="h-3.5 w-3.5" />
        </Button>
        <div className="w-px h-5 bg-border mx-0.5" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={handlePasteFromClipboard}
          title="Paste from Clipboard (preserves HTML)"
        >
          <ClipboardPaste className="h-3.5 w-3.5" />
        </Button>
        <div className="flex-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={`h-7 px-2 text-xs ${isHtmlMode ? "bg-muted" : ""}`}
          onClick={toggleHtmlMode}
          title="Toggle HTML Source"
        >
          <Code className="h-3.5 w-3.5 mr-1" />
          HTML
        </Button>
      </div>

      {/* Editor / HTML Source / Raw Preview */}
      {isHtmlMode ? (
        <textarea
          className="w-full min-h-[120px] p-3 text-xs font-mono bg-background resize-y focus:outline-none"
          value={htmlSource}
          onChange={(e) => {
            setHtmlSource(e.target.value);
          }}
          disabled={disabled}
          spellCheck={false}
        />
      ) : rawHtml ? (
        // Raw HTML preview via iframe for complete CSS isolation
        <div
          className="relative min-h-[120px] cursor-text"
          onClick={handleRawPreviewClick}
        >
          <iframe
            ref={iframeRef}
            srcDoc={iframeSrcDoc}
            className="w-full border-0 pointer-events-none"
            style={{ minHeight: "120px" }}
            title="Signature preview"
            onLoad={handleIframeLoad}
          />
        </div>
      ) : (
        <EditorContent
          editor={editor}
          className="prose prose-sm max-w-none min-h-[120px] p-3 focus-within:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[100px] [&_.ProseMirror_p]:my-1 [&_.ProseMirror_img]:max-w-[200px] [&_.ProseMirror_img]:h-auto [&_.ProseMirror_table]:border-collapse [&_.ProseMirror_td]:border [&_.ProseMirror_td]:border-gray-300 [&_.ProseMirror_td]:p-2 [&_.ProseMirror_th]:border [&_.ProseMirror_th]:border-gray-300 [&_.ProseMirror_th]:p-2 [&_.ProseMirror_th]:bg-muted"
        />
      )}
    </div>
  );
};
